import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
export function AccountPanel() {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["profile"],
    queryFn: () => api<{ name: string; username: string; timezone: string }>("/profile"),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("UTC");
  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNew] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  useEffect(() => {
    if (query.data) {
      setName(query.data.name);
      setTimezone(query.data.timezone);
    }
  }, [query.data]);
  async function submit(kind: "profile" | "password" | "logout"): Promise<void> {
    setPending(true);
    setError("");
    setMessage("");
    try {
      if (kind === "profile") {
        await api("/profile", { method: "PATCH", body: JSON.stringify({ name, timezone }) });
        await client.invalidateQueries({ queryKey: ["workspace"] });
        await client.invalidateQueries({ queryKey: ["profile"] });
        setMessage("Profile saved.");
      } else if (kind === "password") {
        await api("/auth/change-password", {
          method: "POST",
          body: JSON.stringify({ currentPassword, newPassword, revokeOtherSessions: true }),
        });
        setCurrent("");
        setNew("");
        setMessage("Password changed. Other sessions have been signed out.");
      } else {
        await api("/auth/sign-out", { method: "POST", body: "{}" });
        client.clear();
        location.reload();
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "The change could not be saved.");
    } finally {
      setPending(false);
    }
  }
  return (
    <section className="account-panel">
      <h3>Your account</h3>
      {query.error && <p className="inline-alert">{query.error.message}</p>}
      {query.data && (
        <>
          <p className="form-note">Signed in as {query.data.username}</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit("profile");
            }}
          >
            <label className="form-label">
              Display name
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
              />
            </label>
            <label className="form-label">
              Display time zone
              <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                {[...new Set([timezone, "UTC", ...Intl.supportedValuesOf("timeZone")])].map(
                  (zone) => (
                    <option value={zone} key={zone}>
                      {zone.replaceAll("_", " ")}
                    </option>
                  ),
                )}
              </select>
            </label>
            <button className="button secondary" disabled={pending}>
              Save profile
            </button>
          </form>
          <details className="password-settings">
            <summary>Change password</summary>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submit("password");
              }}
            >
              <label className="form-label">
                Current password
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrent(e.target.value)}
                />
              </label>
              <label className="form-label">
                New password
                <input
                  type="password"
                  required
                  minLength={12}
                  maxLength={128}
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNew(e.target.value)}
                />
              </label>
              <button className="button secondary" disabled={pending}>
                Change password
              </button>
            </form>
          </details>
        </>
      )}
      {error && (
        <p role="alert" className="inline-alert">
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="form-note">
          {message}
        </p>
      )}
      <button
        className="button secondary signout-button"
        disabled={pending}
        onClick={() => {
          void submit("logout");
        }}
      >
        Sign out of Workroom
      </button>
    </section>
  );
}
